import {
  GlobalSearchResult,
  NavigationSearchResult,
  globalSearch,
} from '@/api/global-search';
import { Asset } from '@/asset/api/types';
import { GlobalSearchInput } from '@/components/global-search-input';
import { GlobalSearchResults } from '@/components/global-search-results';
import { SearchRounded } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { Location } from '@/location/api/types';
import { Project } from '@/project/api/types';
import { PurchaseOrder } from '@/purchase-order/api/types';
import { ReturnOrder } from '@/return-order/api/types';
import { Shipment } from '@/shipment/api/types';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Slide,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const MAX_RESULTS_PER_CATEGORY = 5;

/**
 * Creates a flattened array of search results in the same order used for navigation
 */
function createFlattenedResults(
  data: GlobalSearchResult | undefined
): (
  | Asset
  | Location
  | Customer
  | Project
  | PurchaseOrder
  | FulfillmentOrder
  | ReturnOrder
  | Shipment
  | NavigationSearchResult
)[] {
  if (!data) return [];

  return [
    ...data.navigation.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.assets.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.locations.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.customers.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.projects.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.purchaseOrders.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.fulfillmentOrders.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.returnOrders.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.inboundShipments.slice(0, MAX_RESULTS_PER_CATEGORY),
    ...data.outboundShipments.slice(0, MAX_RESULTS_PER_CATEGORY),
  ];
}

/**
 * GlobalSearch component
 * Responsive: On xs screens, shows only a magnifying glass icon by default. When clicked, expands to show the full search bar.
 * On sm and up, always shows the full search bar.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const isBelowLg = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('lg')
  );

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setAnchorEl(searchBoxRef.current);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const closeSearch = useCallback(() => {
    setInputValue('');
    setSelectedIndex(-1);
    setSearchOpen(false);
  }, []);

  useKeyboardShortcut(
    {
      key: 'k',
      metaKey: true,
      ctrlKey: true,
      preventDefault: true,
    },
    () => {
      openSearch();
    }
  );

  const { data, isFetching } = useQuery({
    queryKey: ['globalSearch', searchQuery],
    queryFn: () => {
      if (!searchQuery || searchQuery.length < 2) {
        return {
          assets: [],
          locations: [],
          customers: [],
          projects: [],
          purchaseOrders: [],
          fulfillmentOrders: [],
          returnOrders: [],
          inboundShipments: [],
          outboundShipments: [],
          navigation: [],
        };
      }
      return globalSearch({ query: searchQuery });
    },
    enabled: searchQuery.length >= 2,
    placeholderData: keepPreviousData,
  });

  // Show loading when fetching new results or when user has typed something
  // that will trigger a new search (during debounce period)
  const isSearchPending =
    isFetching ||
    (inputValue.trim() !== searchQuery.trim() && inputValue.trim().length >= 2);

  // Memoize flattened results to prevent recalculation on every keypress
  const flattenedResults = useMemo(() => createFlattenedResults(data), [data]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(inputValue);
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue]);

  useEffect(() => {
    setSelectedIndex(flattenedResults.length > 0 ? 0 : -1);
  }, [flattenedResults]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    // Always open results when typing
    if (!searchOpen) openSearch();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      closeSearch();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (flattenedResults.length > 0) {
        setSelectedIndex((prevIndex) =>
          prevIndex < flattenedResults.length - 1 ? prevIndex + 1 : prevIndex
        );
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : prevIndex
      );
    } else if (
      event.key === 'Enter' &&
      selectedIndex >= 0 &&
      flattenedResults.length > 0 &&
      data
    ) {
      event.preventDefault();

      if (selectedIndex < flattenedResults.length) {
        const selected = flattenedResults[selectedIndex];
        let url = '';

        if ('to' in selected) {
          url = selected.to;
        } else {
          const item = selected as { key: string };

          if ('serial' in selected) {
            url = `/assets/${item.key}`;
          } else if ('googlePlaceId' in selected) {
            url = `/locations/${item.key}`;
          } else if ('logoFileKey' in selected) {
            url = `/customers/${item.key}`;
          } else if ('totalContractValue' in selected) {
            url = `/projects/${item.key}`;
          } else if ('purchaseOrderNumber' in selected) {
            url = `/purchase-orders/${item.key}`;
          } else if ('fulfillmentOrderNumber' in selected) {
            url = `/fulfillments/${item.key}`;
          } else if ('returnOrderNumber' in selected) {
            url = `/return-orders/${item.key}`;
          } else if ('shipmentNumber' in selected) {
            const shipment = selected as Shipment;
            const shipmentType = shipment.shipmentType ?? 'inbound';
            url = `/shipments?type=${shipmentType}&query=${encodeURIComponent(shipment.shipmentNumber)}`;
          }
        }

        if (url) {
          closeSearch();
          navigate({ to: url });
        }
      }
    }
  };

  const handleResultClick = useCallback(
    (url: string, event: MouseEvent) => {
      event.preventDefault();
      closeSearch();
      navigate({ to: url });
    },
    [navigate, closeSearch]
  );

  // Responsive rendering logic: always render the input, but change its container and visibility
  return (
    <>
      {isBelowLg ? (
        <>
          {/* IconButton for mobile/compact view */}
          <IconButton
            color="inherit"
            onClick={() => {
              openSearch();
              setTimeout(() => {
                inputRef.current?.focus();
              }, 0);
            }}
            aria-label="Open search"
            sx={{ marginLeft: 1 }}
          >
            <SearchRounded sx={{ color: 'text.primary' }} />
          </IconButton>
          {/* Slide-down Paper for mobile/compact view */}
          <Slide direction="down" in={searchOpen} mountOnEnter unmountOnExit>
            <Paper
              elevation={4}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                zIndex: 1400,
                borderRadius: 0,
                padding: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingX: 2,
              }}
            >
              <ClickAwayListener onClickAway={closeSearch}>
                <Box ref={searchBoxRef} sx={{ flex: 1, width: '100%' }}>
                  <GlobalSearchInput
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onClose={closeSearch}
                    isOpen={searchOpen}
                    inputRef={inputRef}
                  />
                  {/* Render results directly below input on mobile */}
                  {inputValue.trim().length > 0 &&
                    (isSearchPending || data) && (
                      <GlobalSearchResults
                        isLoading={isSearchPending}
                        searchQuery={searchQuery}
                        entityResults={
                          data ?? {
                            assets: [],
                            locations: [],
                            customers: [],
                            projects: [],
                            purchaseOrders: [],
                            fulfillmentOrders: [],
                            returnOrders: [],
                            inboundShipments: [],
                            outboundShipments: [],
                            navigation: [],
                          }
                        }
                        maxResults={MAX_RESULTS_PER_CATEGORY}
                        onResultClick={handleResultClick}
                        selectedIndex={selectedIndex}
                        sx={{
                          width: '100%',
                          maxWidth: '100vw',
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Slide>
        </>
      ) : (
        <>
          {/* Inline search bar for lg and up, or for mobile when expanded */}
          <Box
            ref={searchBoxRef}
            sx={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <GlobalSearchInput
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onClose={closeSearch}
              isOpen={searchOpen}
              inputRef={inputRef}
            />
            {/* Popper for results, only show on lg and up */}
            <ClickAwayListener onClickAway={closeSearch}>
              <Popper
                open={searchOpen && Boolean(anchorEl) && !!inputValue.trim()}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1500, width: 400 }}
              >
                {(isSearchPending || data) && (
                  <GlobalSearchResults
                    isLoading={isSearchPending}
                    searchQuery={searchQuery}
                    entityResults={
                      data ?? {
                        assets: [],
                        locations: [],
                        customers: [],
                        projects: [],
                        purchaseOrders: [],
                        fulfillmentOrders: [],
                        returnOrders: [],
                        inboundShipments: [],
                        outboundShipments: [],
                        navigation: [],
                      }
                    }
                    maxResults={MAX_RESULTS_PER_CATEGORY}
                    onResultClick={handleResultClick}
                    selectedIndex={selectedIndex}
                  />
                )}
              </Popper>
            </ClickAwayListener>
          </Box>
        </>
      )}
    </>
  );
}
